import moment from 'moment'
import type { Moment } from 'moment'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'
import { OutputType } from '@/app/constants/outputType'
import type { DatePickerProps, DatePickerRangeProps } from '@/modules/drivers/types'
import { useDateStore } from '@/modules/drivers/stores/dateStore'
import { zustandPlugin } from '@/modules/shared/openreplay'

const toDatePickerProps = (m: Moment): DatePickerProps => ({
  fullDate: m.format('YYYY-MM-DD'),
  year: m.year(),
  month: m.month() + 1,
  day: m.date(),
})

const getDefaultDateRange = (): DatePickerRangeProps => {
  const storeRange = useDateStore.getState().range
  if (storeRange) return storeRange

  const storedMonth = useDateStore.getState().month
  if (storedMonth) {
    const [year, month] = storedMonth.split('-').map(Number)
    const first = moment({ year, month: month - 1, day: 1 })
    const last = moment({ year, month: month - 1 }).endOf('month')
    return {
      startDate: toDatePickerProps(first),
      endDate: toDatePickerProps(last),
    }
  }

  const now = moment()
  const monthAgo = moment().subtract(1, 'month')
  return {
    startDate: toDatePickerProps(monthAgo),
    endDate: toDatePickerProps(now),
  }
}

type State = {
  checkedDrivers: string[]
  dateRange: DatePickerRangeProps
  outputType: OutputType
}

type Actions = {
  setCheckedDrivers: (drivers: string[]) => void
  setDateRange: (range: DatePickerRangeProps) => void
  syncDateRange: () => void
  setOutputType: (type: OutputType) => void
}

export const useExportStore = create<State & Actions>()(
  devtools(
    zustandPlugin((set) => ({
      checkedDrivers: [],
      dateRange: getDefaultDateRange(),
      outputType: OutputType.Pdf,
      setCheckedDrivers: (checkedDrivers) =>
        set({ checkedDrivers }, false, 'setCheckedDrivers'),
      setDateRange: (dateRange) =>
        set({ dateRange }, false, 'setDateRange'),
      syncDateRange: () =>
        set({ dateRange: getDefaultDateRange() }, false, 'syncDateRange'),
      setOutputType: (outputType) =>
        set({ outputType }, false, 'setOutputType'),
    }), 'export'),
    { name: 'EasyTacho', store: 'export' },
  ),
)

export const useExport = () =>
  useExportStore(
    useShallow((s) => ({
      checkedDrivers: s.checkedDrivers,
      setCheckedDrivers: s.setCheckedDrivers,
      dateRange: s.dateRange,
      setDateRange: s.setDateRange,
      syncDateRange: s.syncDateRange,
      outputType: s.outputType,
      setOutputType: s.setOutputType,
    })),
  )
